// Deterministic resting layout for a board cell (Positions D5, coordinate-free
// board). The world pose of every resting in-play card is a pure function of
// server state. Cell, region (surface/subsurface), isSite, stackSeq. Plus the
// static grid geometry. Both clients compute identical layouts, so nothing
// about a resting pose ever needs to cross the wire, and the per-frame OBB/SAT
// overlap rank scan is unnecessary: Y is just the card's index in its cell.
//
// Layout shape per cell:
//   - The site snaps to its controller's near edge (siteSnapPoint, unchanged).
//   - Units cascade diagonally in PER-CONTROLLER lanes: each player's cards
//     start just off the cell center on THEIR side and cascade outward toward
//     their near edge. The opponent's stack is the same lane mirrored 180°
//     through the center, so every square reads as "my stack / their stack".
//     Spacing keeps a grabbable sliver per card and compresses as the count
//     grows so a cascade never leaves the cell.
//   - Subsurface lanes mirror to the opposite side of their controller's
//     surface lane, so buried cards peek out from under the other edge.
//   - Y stacks by within-cell rank: (region tier, stackSeq) ascending. Ranks
//     start one layer above the table so a flat aura sheet at the table layer
//     never z-fights the bottom card of a stack.
//
// Region tiers mirror the established render convention (stackPhysics): in
// surface view subsurface < site < surface; the descend view flips subsurface
// above the site so those cards are visually on top.

import { CARD_THICKNESS, CARD_WIDTH, CARD_HEIGHT } from './cardMesh';
import { siteSnapPoint } from './engine/core/sitePlacement';

export const TABLE_Y = 0.05;
export const LAYER_GAP = 0.005;
const CELL_BASE_RANK = 1;

// Fraction of the cell half-extent between cascade neighbours at low counts,
// and the maximum half-span the cascade may occupy at high counts.
const CASCADE_STEP_FRAC = 0.34;
const CASCADE_MAX_FRAC = 0.85;
// The Z component of the diagonal is shallower than X so a long cascade reads
// as a fanned row rather than a column marching off the cell's near edge.
const CASCADE_Z_RATIO = 0.6;
// Where a controller's lane STARTS, as a fraction of the half-extent off the
// cell center toward their side. Keeps the two players' stacks visibly
// apart even when each holds a single card.
const CASCADE_START_FRAC = 0.22;
// Minimum world-space gap between a resting card's edge and its square's
// border (the border clamp in layoutCellRest).
const CASCADE_EDGE_PAD = 0.4;

export function regionTier(card, subsurfaceView) {
  if (subsurfaceView) {
    if (card.subsurface) return 2;
    if (card.isSite) return 1;
    return 0;
  }
  if (card.subsurface) return 0;
  if (card.isSite) return 1;
  return 2;
}

// Within-cell resting order: region tier, then tokens above full-size cards
// (a half-scale token under a full card is invisible AND unpickable, since
// the raycast pick follows physical order, and every re-placement re-bumps
// stackSeq, so an Avatar walking onto its square would otherwise cover the
// token permanently. #98), then placement sequence (a missing stackSeq sorts
// first. Pre-migration snapshots), then id for total order.
export function sortCellMembers(members, subsurfaceView = false) {
  return [...members].sort((a, b) => {
    const ta = regionTier(a, subsurfaceView);
    const tb = regionTier(b, subsurfaceView);
    if (ta !== tb) return ta - tb;
    const ka = a.isToken ? 1 : 0;
    const kb = b.isToken ? 1 : 0;
    if (ka !== kb) return ka - kb;
    const sa = a.stackSeq ?? 0;
    const sb = b.stackSeq ?? 0;
    if (sa !== sb) return sa - sb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

export function restY(rank) {
  return TABLE_Y + CARD_THICKNESS / 2 + rank * (CARD_THICKNESS + LAYER_GAP);
}

// ── Focused-cell fan ────────────────────────────────────────────────────────
// When a cell is FOCUSED (clicked), its cards fly out of the stack into a fan
// centered on the cell. Lifted well above every resting stack so each card
// is individually visible and hittable with all of its live presentation
// (tap rotation, statuses, stat badges, equipment) intact. Like the resting
// cascade this is a PURE layout: collapse recomputes the rest layout, and a
// server diff mid-fan folds members straight into their fan slots. There is
// no saved pose anywhere. Unlike the cascade the fan is LOCAL presentation
// (each client fans independently), so it may use the viewer's orientation.

export const FAN_Y = 3.0;
// Nominal upright cards per fan row. Rows actually wrap on WIDTH, so
// tapped / landscape cards take more of the budget and wrap earlier.
export const FAN_ROW_MAX = 5;
const FAN_PAD = CARD_WIDTH * 0.14;
const FAN_MAX_ROW_W = FAN_ROW_MAX * CARD_WIDTH + (FAN_ROW_MAX - 1) * FAN_PAD;
const FAN_ROW_GAP = CARD_HEIGHT * 0.12;
const FAN_BOW = CARD_HEIGHT * 0.16;
// Extra corridor between the surface and subsurface blocks. The divider
// line + headings render inside it.
const FAN_GROUP_GAP = CARD_HEIGHT * 0.6;

// A fanned card's XZ footprint. Sites render landscape and tapping rotates
// 90°, so either flips the footprint (a tapped site is back to portrait).
function fanFootprint(m) {
  const swapped = !!m.isSite !== !!m.tapped;
  return swapped ? { w: CARD_HEIGHT, h: CARD_WIDTH } : { w: CARD_WIDTH, h: CARD_HEIGHT };
}

// Lay one group's rows. Rows pack greedily on WIDTH (footprint + padding, so
// nothing ever overlaps regardless of orientation) and each row is a shallow
// hand-of-cards arc. `zOff` is the group's starting edge in local (dir)
// space. Returns { height, maxWidth } of the laid block.
function layoutFanGroup(out, group, cx, cz, dir, zOff) {
  const rows = [];
  let row = null;
  for (const m of group) {
    const fp = fanFootprint(m);
    if (!row || row.width + FAN_PAD + fp.w > FAN_MAX_ROW_W) {
      row = { items: [], width: 0, maxH: 0 };
      rows.push(row);
    }
    if (row.items.length > 0) row.width += FAN_PAD;
    row.items.push({ m, fp, x: row.width + fp.w / 2 });
    row.width += fp.w;
    row.maxH = Math.max(row.maxH, fp.h);
  }
  let zCursor = zOff;
  let maxWidth = 0;
  for (const r of rows) {
    maxWidth = Math.max(maxWidth, r.width);
    const rowCenter = zCursor + r.maxH / 2;
    for (const it of r.items) {
      const xc = it.x - r.width / 2;
      const t = r.width > 0 ? xc / (r.width / 2) : 0;
      out.set(it.m.id, {
        x: cx + xc,
        y: FAN_Y,
        z: cz + dir * (rowCenter + t * t * FAN_BOW),
      });
    }
    zCursor += r.maxH + FAN_ROW_GAP;
  }
  const height = rows.length
    ? zCursor - zOff - FAN_ROW_GAP + FAN_BOW
    : 0;
  return { height, maxWidth };
}

/**
 * Fan poses + chrome metadata for a focused cell's members.
 *
 * The fan splits into a SURFACE block (sites + surface units) on top and a
 * SUBSURFACE block below, separated by a corridor where the caller renders
 * a divider line + headings. Each block wraps into arc rows of FAN_ROW_MAX.
 * `rotated` flips the whole layout toward the local viewer's near edge.
 * Order inside each block follows the sortCellMembers total order, so rows
 * read left-to-right in stack order.
 *
 * @returns {{ poses: Map<string,{x,y,z}>, meta: object|null }} meta carries
 *   the world-space frame for the scrim + divider overlay:
 *   { cx, cz, dir, width, top, bottom, dividerZ|null, surfaceTopZ|null,
 *     subsurfaceTopZ|null }. Top/bottom/…Z are world z values.
 */
export function layoutCellFanEx(geo, members, { subsurfaceView = false, rotated = false } = {}) {
  const poses = new Map();
  if (!geo || !members?.length) return { poses, meta: null };
  const cx = (geo.tl.x + geo.br.x) / 2;
  const cz = (geo.tl.z + geo.br.z) / 2;
  const sorted = sortCellMembers(members, subsurfaceView);
  const dir = rotated ? -1 : 1;

  const surface = sorted.filter((m) => !m.subsurface);
  const subsurface = sorted.filter((m) => m.subsurface);
  const hasBoth = surface.length > 0 && subsurface.length > 0;

  // Measure both blocks (dry run) to center the whole assembly, then lay
  // them for real: surface block, divider corridor, subsurface block.
  const measure = layoutFanGroup(new Map(), surface, 0, 0, 1, 0);
  const measureSub = layoutFanGroup(new Map(), subsurface, 0, 0, 1, 0);
  const extent = measure.height + (hasBoth ? FAN_GROUP_GAP : 0) + measureSub.height;
  const start = -extent / 2;

  layoutFanGroup(poses, surface, cx, cz, dir, start);
  const subStart = start + measure.height + (hasBoth ? FAN_GROUP_GAP : 0);
  layoutFanGroup(poses, subsurface, cx, cz, dir, subStart);

  const meta = {
    cx,
    cz,
    dir,
    width: Math.max(measure.maxWidth, measureSub.maxWidth),
    top: cz + dir * start,
    bottom: cz + dir * (start + extent),
    // Center of the corridor between the two blocks.
    dividerZ: hasBoth ? cz + dir * (start + measure.height + FAN_GROUP_GAP / 2) : null,
    surfaceTopZ: surface.length > 0 ? cz + dir * start : null,
    subsurfaceTopZ: subsurface.length > 0 ? cz + dir * subStart : null,
    hasBoth,
  };
  return { poses, meta };
}

/**
 * Fan poses only (see layoutCellFanEx). Kept as the stable signature for
 * callers/tests that don't need the chrome metadata.
 */
export function layoutCellFan(geo, members, opts = {}) {
  return layoutCellFanEx(geo, members, opts).poses;
}

/**
 * Compute the resting pose of every card on one cell.
 *
 * @param geo     cell corners {tl,tr,bl,br} from sampleCellGeometry
 * @param members [{id, isSite, isToken, subsurface, stackSeq, rotated, tapped}].
 *                rotated is the card CONTROLLER's orientation (server state),
 *                used for the site snap edge + lane side; tapped widens the
 *                oriented footprint the border clamp respects.
 * @param opts    {subsurfaceView}. Flips region tiers for the descend view.
 * @returns Map(id -> {x, z, y, rank})
 */
export function layoutCellRest(geo, members, { subsurfaceView = false } = {}) {
  const out = new Map();
  if (!geo || !members?.length) return out;
  const cx = (geo.tl.x + geo.br.x) / 2;
  const cz = (geo.tl.z + geo.br.z) / 2;
  const halfW = Math.abs(geo.tr.x - geo.tl.x) / 2;
  const halfH = Math.abs(geo.bl.z - geo.tl.z) / 2;

  const sorted = sortCellMembers(members, subsurfaceView);
  // Per-controller, per-region lanes: the opponent's lane is the local lane
  // mirrored 180° through the cell center.
  const laneKey = (m) => `${m.subsurface ? 'sub' : 'surf'}:${m.rotated ? 'p2' : 'p1'}`;
  const lanes = new Map();
  for (const m of sorted) {
    if (m.isSite) continue;
    const key = laneKey(m);
    let arr = lanes.get(key);
    if (!arr) lanes.set(key, (arr = []));
    arr.push(m);
  }
  // Border guarantee: a card must NEVER exceed its square. The lane's reach
  // is capped so the WIDEST member's oriented footprint (tapped = quarter-
  // turned) fits on both axes of the diagonal, and the whole cascade
  // COMPRESSES into that reach. Every card keeps a distinct sliver instead
  // of the last ones piling on the border.
  const zRatio = CASCADE_Z_RATIO * (halfH / halfW);
  const laneGeoms = new Map();
  for (const [key, arr] of lanes) {
    let maxFw = CARD_WIDTH;
    let maxFh = CARD_HEIGHT;
    for (const lm of arr) {
      if (lm.tapped) {
        maxFw = Math.max(maxFw, CARD_HEIGHT);
        maxFh = Math.max(maxFh, CARD_WIDTH);
      }
    }
    const capX = Math.max(0, halfW - maxFw / 2 - CASCADE_EDGE_PAD);
    const capZ = zRatio > 0 ? Math.max(0, (halfH - maxFh / 2 - CASCADE_EDGE_PAD) / zRatio) : Infinity;
    const maxOff = Math.min(CASCADE_MAX_FRAC * halfW, capX, capZ);
    const start = Math.min(CASCADE_START_FRAC * halfW, maxOff);
    const step = arr.length <= 1
      ? 0
      : Math.min(CASCADE_STEP_FRAC * halfW, (maxOff - start) / (arr.length - 1));
    laneGeoms.set(key, { start, step });
  }

  sorted.forEach((m, i) => {
    const rank = CELL_BASE_RANK + i;
    let x;
    let z;
    if (m.isSite) {
      const snap = siteSnapPoint(geo, !!m.rotated);
      x = snap ? snap.x : cx;
      z = snap ? snap.z : cz;
    } else {
      const lane = lanes.get(laneKey(m));
      const idx = lane.indexOf(m);
      const { start, step } = laneGeoms.get(laneKey(m));
      // Controller sign mirrors the opponent's lane 180°; the subsurface
      // lane flips to the controller's far side so buried cards peek out
      // from under the opposite edge.
      const dir = (m.rotated ? -1 : 1) * (m.subsurface ? -1 : 1);
      const off = start + idx * step;
      x = cx + dir * off;
      z = cz + dir * off * zRatio;
    }
    out.set(m.id, { x, z, y: restY(rank), rank });
  });
  return out;
}
